/**
 * /users — admin user management, styled after the approved mockup: underline
 * tabs (All Users / Active Sessions), rounded filter chips, avatar status dots,
 * grouped active sessions, and a custom footer/pager. Built purely around the
 * eastview-os endpoints that exist:
 *
 *   Users (the /users router — useUsersApi, LOS client/dsHttp):
 *     GET  /users/all_users               → the grid
 *     POST /users/create_user             → Add User
 *     POST /users/update_user_by_id/:id   → Edit
 *
 *   Admin actions (the /auth router — useAdminUsersApi, LOS client):
 *     POST /auth/admin/users/:id/set-password → Set Password (also forces re-login)
 *     GET  /auth/admin/users/:id/activity     → per-user Activity drawer
 *     POST /auth/admin/impersonate            → Impersonate (token issued, not applied here)
 *     GET  /auth/admin/active_sessions        → "Active Sessions" view (who's online)
 *
 * Data (users + sessions) is fetched once at the root so both tab counts and the
 * "Online now" filter work. No Delete / standalone Revoke — the backend exposes
 * neither.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDown";
import MoreVertRounded from "@mui/icons-material/MoreVert";
import RefreshRounded from "@mui/icons-material/Refresh";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { AgGridReact } from "ag-grid-react";
import { useSnackbar } from "notistack";

import {
  useAdminUsersApi,
  useActiveSessionsApi,
  useUsersApi,
  ROLE_OPTIONS,
  roleLabel,
} from "../../hooks/useAdminUsersHook";
import { useCorrespondentsApi } from "../../hooks/useCorrespondentsHook";
import { useGroupsApi } from "../../hooks/useGroupsHook";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useIsInUserActivityGroup } from "../../hooks/useIsInUserActivityGroup";
import UserActivityView from "./UserActivity/UserActivityView";
import {
  slugFromView,
  usersTabPath,
  viewFromSlug,
  visibleUsersTabs,
} from "./usersTabs";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import Flx from "../../components/layout/Flx";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import SideDrawer from "../../components/modals/SideDrawer";
import JsonPreview from "../../components/common/JsonPreview";
import { useAuth } from "../../auth/AuthProvider";
import {
  formatDateTime,
  normalizeIsoStringToUtc,
} from "../../_src_shared/utils/functions/formatDateTime";
import {
  T,
  avatarColor,
  initialsFromName,
} from "../SupportScreen/components/supportUi";

const ALL = "__all__";
const PAGE_SIZES = [15, 25, 50, 100];

// ── helpers ──────────────────────────────────────────────────────────────────
const fullName = (u) =>
  `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "—";
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const userId = (u) => String(u?._id ?? u?.id ?? "");

// Backend timestamps are UTC but often arrive WITHOUT a Z/offset (e.g.
// "2026-06-03T01:58:09.906000"), which new Date() would mis-parse as local time
// and throw "min ago" off by the tz offset. Normalize to UTC first (same rule as
// formatDateTime → normalizeIsoStringToUtc) for all time math.
const dateMs = (v) => {
  if (!v) return 0;
  const t = new Date(
    typeof v === "string" ? normalizeIsoStringToUtc(v) : v,
  ).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const sessionToUser = (s) => ({
  _id: s?.userId,
  emailAddress: s?.userEmail,
  firstName: (s?.name || "").split(" ")[0] || "",
  lastName: (s?.name || "").split(" ").slice(1).join(" "),
  roles: s?.roles || [],
});

const timeAgo = (s) => {
  const ms = dateMs(s);
  if (!ms) return "—";
  const diff = (Date.now() - ms) / 1000;
  if (diff < 0) return formatDateTime(s, "date");
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return "Yesterday";
  return formatDateTime(s, "date");
};

const expiresLabel = (d) => {
  const ms = dateMs(d);
  if (!ms) return null;
  const days = Math.round((ms - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days === 0) return "today";
  return `in ${days} day${days === 1 ? "" : "s"}`;
};

// Compact "Chrome · Windows" from a user-agent string.
const parseUA = (ua) => {
  if (!ua) return "";
  const browser = /Edg/.test(ua)
    ? "Edge"
    : /OPR|Opera/.test(ua)
      ? "Opera"
      : /Chrome|CriOS/.test(ua)
        ? "Chrome"
        : /Firefox|FxiOS/.test(ua)
          ? "Firefox"
          : /Safari/.test(ua)
            ? "Safari"
            : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /iPhone|iPad|iOS/.test(ua)
      ? "iOS"
      : /Mac OS X|Macintosh/.test(ua)
        ? "macOS"
        : /Android/.test(ua)
          ? "Android"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return os ? `${browser} · ${os}` : browser;
};

const eventTone = (e) =>
  !e
    ? "slate"
    : /fail|denied|revoke|error/i.test(e)
      ? "red"
      : /reset|password|otp|magic/i.test(e)
        ? "amber"
        : "slate";

const roleChipsFrom = (items) => {
  const counts = {};
  for (const it of items)
    for (const r of asArray(it.roles)) counts[r] = (counts[r] || 0) + 1;
  const present = ROLE_OPTIONS.filter((r) => counts[r]);
  const extra = Object.keys(counts).filter((r) => !ROLE_OPTIONS.includes(r));
  return [...present, ...extra].map((r) => ({
    value: r,
    label: roleLabel(r),
    count: counts[r],
  }));
};

// Map every user (by _id AND by lowercased email) to the names of the org groups
// they belong to. Group members carry userId (for matched users) and/or an email,
// so we key on both — an unmatched member still lines up by address. Built once
// from the /groups list and shared by the All Users + Active Sessions grids.
const buildUserGroupsIndex = (groups) => {
  const byId = new Map();
  const byEmail = new Map();
  const push = (map, key, name) => {
    if (!key) return;
    const arr = map.get(key) || [];
    if (!arr.includes(name)) arr.push(name);
    map.set(key, arr);
  };
  for (const g of groups || [])
    for (const m of g.members || []) {
      push(byId, String(m.userId || ""), g.name);
      push(byEmail, (m.email || "").toLowerCase(), g.name);
    }
  return { byId, byEmail };
};

const groupsForRow = (index, { id, email }) => {
  if (!index) return [];
  const a = index.byId.get(String(id || "")) || [];
  const b = index.byEmail.get((email || "").toLowerCase()) || [];
  return [...new Set([...a, ...b])];
};

const pageItems = (page, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const set = new Set([1, 2, pageCount, page - 1, page, page + 1]);
  const nums = [...set]
    .filter((n) => n >= 1 && n <= pageCount)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of nums) {
    if (n - prev > 1) out.push("…");
    out.push(n);
    prev = n;
  }
  return out;
};

// ── primitives ───────────────────────────────────────────────────────────────
const Tag = ({ label, tone = "slate" }) => {
  const tones = {
    slate: { bg: "#f1f4f6", fg: T.ink700 },
    teal: { bg: T.teal50, fg: T.teal600 },
    purple: { bg: "#f3eefe", fg: "#7c3aed" },
    indigo: { bg: "#eef2ff", fg: "#4f46e5" },
    amber: { bg: "#fef3e2", fg: T.amber },
    red: { bg: "#fdecec", fg: T.red },
  };
  const m = tones[tone] || tones.slate;
  return (
    <Box
      component="span"
      sx={{
        fontSize: "11.5px",
        fontWeight: 600,
        color: m.fg,
        background: m.bg,
        borderRadius: "6px",
        px: "8px",
        py: "3px",
        whiteSpace: "nowrap",
        lineHeight: 1.6,
        flexShrink: 0,
        // A lone chip in a narrow column ellipsises rather than running past
        // the cell (the tag rows below hand it the room they measured).
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Box>
  );
};

const TAG_GAP = 5; // px between chips — keep in sync with the `gap` below
const OVERFLOW_CHIP_W = 40; // room reserved for the trailing "+N" chip

// Tag row for grid cells. The chips are measured against the cell they're in:
// whatever doesn't fit collapses into a trailing "+N" chip, so a long list can
// never be cut mid-chip or painted over the next column. `title` keeps the full
// list one hover away.
const TagCell = ({ tags, title }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  // `null` = measuring: every chip is rendered (clipped) so it can be measured.
  const [visible, setVisible] = useState(null);

  // The cell width drives everything, and it changes on column resize. Seed it
  // synchronously here (before paint) so a cell never flashes its full chip row
  // while waiting for the observer's first async callback.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(Math.floor(el.clientWidth));
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) =>
      setWidth(Math.floor(entry.contentRect.width)),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // New width or new content → back to the measuring pass.
  useLayoutEffect(() => setVisible(null), [width, title]);

  useLayoutEffect(() => {
    if (visible !== null || !width) return;
    const el = ref.current;
    if (!el) return;
    const chips = Array.from(el.querySelectorAll("[data-tag]"));
    let used = 0;
    let fit = 0;
    for (const chip of chips) {
      const next = used + (fit ? TAG_GAP : 0) + chip.offsetWidth;
      if (next > width) break;
      used = next;
      fit += 1;
    }
    // Hiding anything costs a "+N" chip; buy its room back by dropping chips.
    // One always survives — it ellipsises instead of disappearing.
    while (
      fit > 1 &&
      fit < chips.length &&
      used + TAG_GAP + OVERFLOW_CHIP_W > width
    ) {
      fit -= 1;
      used -= chips[fit].offsetWidth + TAG_GAP;
    }
    setVisible(Math.max(1, fit));
  }, [visible, width, title]);

  if (!tags.length)
    return (
      <Box component="span" sx={{ color: T.ink300 }}>
        —
      </Box>
    );

  const shown = visible === null ? tags : tags.slice(0, visible);
  const hidden = tags.length - shown.length;
  // The sole survivor of a too-narrow column is allowed to shrink so its chip
  // truncates cleanly; otherwise chips keep their natural width.
  const shrinkLast = hidden > 0 && shown.length === 1;

  return (
    <Box
      ref={ref}
      title={title}
      sx={{
        display: "flex",
        gap: `${TAG_GAP}px`,
        alignItems: "center",
        height: "100%",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {shown.map((tag, i) => (
        <Box
          key={tag.key ?? i}
          data-tag=""
          sx={{
            display: "flex",
            minWidth: 0,
            overflow: "hidden",
            flexShrink: shrinkLast ? 1 : 0,
          }}
        >
          {tag}
        </Box>
      ))}
      {hidden > 0 && <Tag label={`+${hidden}`} tone="slate" />}
    </Box>
  );
};

const AVATAR_SIZE = 34;

// Local avatar (instead of the shared UserAvatar) so the initials are dead-center:
// flex + lineHeight:1 avoids the caps-ride-high offset that grid + default
// line-height produces.
const AvatarDot = ({ email, name, status }) => (
  <Box
    sx={{
      position: "relative",
      flexShrink: 0,
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
    }}
  >
    <Box
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: ".02em",
        color: "#fff",
        userSelect: "none",
        background: avatarColor(email || name),
      }}
    >
      {initialsFromName(name || email)}
    </Box>
    {status && (
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 10,
          height: 10,
          borderRadius: "50%",
          border: "2px solid #fff",
          background:
            status === "online"
              ? T.green
              : status === "idle"
                ? T.amber
                : T.ink300,
        }}
      />
    )}
  </Box>
);

const NameCell = ({ name, email, status, tag }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      height: "100%",
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    <AvatarDot email={email} name={name} status={status} />
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "2px",
      }}
    >
      <Box
        sx={{
          fontWeight: 600,
          fontSize: 13.5,
          lineHeight: 1.2,
          color: T.ink900,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </Box>
      {email && (
        <Box
          sx={{
            fontSize: 12,
            lineHeight: 1.2,
            color: T.ink500,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {email}
          </Box>
          {tag && (
            <>
              <Box component="span" sx={{ color: T.ink300 }}>
                ·
              </Box>
              {tag}
            </>
          )}
        </Box>
      )}
    </Box>
  </Box>
);

const ActionLink = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1,
      border: `1px solid ${T.line}`,
      background: "#fff",
      color: T.ink700,
      cursor: "pointer",
      px: "10px",
      py: "6px",
      borderRadius: "7px",
      whiteSpace: "nowrap",
      transition: ".12s",
      "&:hover": {
        color: T.teal600,
        borderColor: T.teal500,
        background: T.teal50,
      },
    }}
  >
    {children}
  </Box>
);

// Kebab (⋮) actions menu — each row owns its own anchor state. `items` is an
// array of { label, onClick, divider? }.
const RowActionsMenu = ({ items, label }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const activeSx = {
    border: `1px solid ${anchor ? T.teal500 : T.line}`,
    borderRadius: "7px",
    color: anchor ? T.teal600 : T.ink700,
    background: anchor ? T.teal50 : "#fff",
    "&:hover": {
      color: T.teal600,
      borderColor: T.teal500,
      background: T.teal50,
    },
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      {label ? (
        <Box
          component="button"
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            ...activeSx,
            font: "inherit",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
            px: "10px",
            py: "6px",
            transition: ".12s",
          }}
        >
          {label}
          <KeyboardArrowDownRounded sx={{ fontSize: 15 }} />
        </Box>
      ) : (
        <IconButton
          size="small"
          aria-label="row actions"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={activeSx}
        >
          <MoreVertRounded sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { minWidth: 168, borderRadius: "10px", boxShadow: T.shadow },
          },
        }}
      >
        {items.map((it, i) => [
          it.divider && i > 0 ? (
            <Divider key={`d${i}`} sx={{ my: 0.5 }} />
          ) : null,
          <MenuItem
            key={it.label}
            onClick={() => {
              setAnchor(null);
              it.onClick();
            }}
            sx={{ fontSize: 13.5, color: T.ink700, py: 0.85 }}
          >
            {it.label}
          </MenuItem>,
        ])}
      </Menu>
    </Box>
  );
};

const Tabs = ({ value, onChange, options }) => (
  <Box
    sx={{
      display: "flex",
      gap: 3,
      borderBottom: `1px solid ${T.line}`,
      mt: 2.5,
    }}
  >
    {options.map((o) => {
      const on = value === o.value;
      return (
        <Box
          key={o.value}
          component="button"
          type="button"
          onClick={() => onChange(o.value)}
          sx={{
            font: "inherit",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            px: "2px",
            py: "10px",
            mb: "-1px",
            color: on ? T.ink900 : T.ink500,
            fontWeight: on ? 700 : 600,
            fontSize: 14,
            borderBottom: `2px solid ${on ? T.teal500 : "transparent"}`,
          }}
        >
          {o.dot && (
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: T.green,
              }}
            />
          )}
          {o.label}
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: on ? T.teal600 : T.ink500,
              background: on ? T.teal50 : "#f1f4f6",
              borderRadius: "999px",
              px: "7px",
              py: "1px",
            }}
          >
            {o.count}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const FilterChips = ({ value, onChange, options }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    {options.map((o) => {
      const on = value === o.value;
      return (
        <Box
          key={o.value}
          component="button"
          type="button"
          onClick={() => onChange(o.value)}
          sx={{
            font: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            cursor: "pointer",
            border: `1px solid ${on ? T.teal500 : T.line}`,
            background: on ? T.teal50 : "#fff",
            color: on ? T.teal600 : T.ink700,
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: "999px",
            px: "12px",
            py: "6px",
            transition: ".12s",
            "&:hover": { borderColor: on ? T.teal500 : T.ink300 },
          }}
        >
          {o.label}
          {o.count != null && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: on ? T.teal600 : T.ink500,
              }}
            >
              {o.count}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

const SearchBar = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      flex: "1 1 260px",
      maxWidth: 360,
    }}
  >
    <SearchRounded
      sx={{
        position: "absolute",
        left: 12,
        fontSize: 19,
        color: T.ink300,
        zIndex: 1,
      }}
    />
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{
        width: "100%",
        height: 40,
        fontSize: 14,
        pl: "40px",
        pr: "12px",
        border: `1px solid ${T.line}`,
        borderRadius: "9px",
        background: "#fff",
        "&.Mui-focused": {
          borderColor: T.teal500,
          boxShadow: `0 0 0 3px rgba(33,169,196,.14)`,
        },
      }}
    />
  </Box>
);

const Footer = ({ summary, page, pageCount, pageSize, onPage, onPageSize }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: "4px",
      pt: 1.5,
      fontSize: 12.5,
      color: T.ink500,
      flexWrap: "wrap",
      gap: 1,
    }}
  >
    <Box>{summary}</Box>
    <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Page size
        <Box
          component="select"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          sx={{
            font: "inherit",
            fontSize: 12.5,
            color: T.ink700,
            border: `1px solid ${T.line}`,
            borderRadius: "7px",
            px: "8px",
            py: "5px",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: "4px" }}>
        {pageItems(page, pageCount).map((it, i) =>
          it === "…" ? (
            <Box key={`e${i}`} sx={{ px: "6px", py: "5px", color: T.ink300 }}>
              …
            </Box>
          ) : (
            <Box
              key={it}
              component="button"
              type="button"
              onClick={() => onPage(it)}
              sx={{
                font: "inherit",
                fontSize: 12.5,
                border: `1px solid ${it === page ? T.teal600 : T.line}`,
                background: it === page ? T.teal600 : "#fff",
                color: it === page ? "#fff" : T.ink700,
                fontWeight: it === page ? 700 : 500,
                borderRadius: "6px",
                px: "10px",
                py: "5px",
                cursor: "pointer",
              }}
            >
              {it}
            </Box>
          ),
        )}
        <Box
          component="button"
          type="button"
          onClick={() => onPage(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
          sx={{
            font: "inherit",
            fontSize: 12.5,
            border: `1px solid ${T.line}`,
            background: "#fff",
            color: page >= pageCount ? T.ink300 : T.ink700,
            borderRadius: "6px",
            px: "10px",
            py: "5px",
            cursor: page >= pageCount ? "default" : "pointer",
          }}
        >
          ›
        </Box>
      </Box>
    </Box>
  </Box>
);

const tealBtnSx = {
  background: T.teal600,
  "&:hover": { background: "#127f97" },
  fontWeight: 600,
  borderRadius: "8px",
  px: 2.25,
};

const HeaderActions = ({ onRefresh, onAdd, addLabel = "Add User" }) => (
  <>
    <Button
      variant="outlined"
      startIcon={<RefreshRounded sx={{ fontSize: 18 }} />}
      onClick={onRefresh}
      sx={{
        color: T.ink700,
        borderColor: T.line,
        background: "#fff",
        fontWeight: 600,
        borderRadius: "8px",
        "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
      }}
    >
      Refresh
    </Button>
    <Button
      variant="contained"
      startIcon={<AddRounded />}
      onClick={onAdd}
      sx={tealBtnSx}
    >
      {addLabel}
    </Button>
  </>
);

const GridCard = ({ children }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      overflow: "hidden",
      // `enableCellTextSelection` (see gridProps) makes ag-grid wrap every cell's
      // content in `.ag-cell-wrapper`, which moves the `ag-cell-value` class —
      // and the core `overflow:hidden` that rides on it — OFF the cell. Without
      // these two rules a wide cell (a row of group chips, a long device string)
      // paints straight over the next column. Clip the cell, and let the wrapper
      // shrink inside it so renderers sized with `flex:1 + minWidth:0` measure
      // the real column width.
      "& .ag-cell": { overflow: "hidden" },
      "& .ag-cell > .ag-cell-wrapper": {
        flex: "1 1 auto",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      },
    }}
  >
    {children}
  </Box>
);

const PageShell = ({ children }) => (
  <Box
    sx={{
      background: T.bg,
      minHeight: "100%",
      height: "100%",
      overflowY: "auto",
      fontFamily: "inherit",
    }}
  >
    {/* Wider than MUI's `xl` (1536px): this screen's grids carry a lot of
        columns and the extra room goes straight into them. */}
    <Container
      maxWidth={false}
      sx={{ maxWidth: "1800px", pt: "30px", pb: "64px" }}
    >
      {children}
    </Container>
  </Box>
);

// `overflow:hidden` is the inline half of the clip GridCard sets up — it lands
// on `.ag-cell` itself, so a cell can never paint over its neighbour.
const centerCell = {
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
};
const leftActions = { ...centerCell, justifyContent: "flex-start" };

const gridProps = {
  defaultColDef: {
    resizable: true,
    sortable: true,
    filter: false,
    minWidth: 90,
  },
  domLayout: "autoHeight",
  rowHeight: 62,
  headerHeight: 42,
  suppressCellFocus: true,
  // Let the mouse select/drag text inside cells (emails, names, ids) so it can be
  // copied. ensureDomOrder keeps the rendered DOM in visual order so a drag
  // across cells selects what you'd expect.
  enableCellTextSelection: true,
  ensureDomOrder: true,
  // ag-grid owns pagination so sort/filter run across the FULL dataset and only
  // then paginate (manual slicing would sort just the visible page). Its own
  // pager UI is suppressed — our custom <Footer> drives it via the grid API.
  pagination: true,
  suppressPaginationPanel: true,
};

// Wires ag-grid's internal pagination to our custom footer. Pass `onGridReady`
// + `onPaginationChanged` to <AgGridReact>; read `pageInfo` for the footer and
// call `goToPage` / `setPageSize` from it.
const useGridPagination = (initialSize = 15) => {
  const [gridApi, setGridApi] = useState(null);
  const [pageSize, setPageSize] = useState(initialSize);
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pageCount: 1,
    rowCount: 0,
  });

  const onGridReady = useCallback((e) => setGridApi(e.api), []);
  const onPaginationChanged = useCallback((e) => {
    if (!e.api) return;
    setPageInfo({
      page: e.api.paginationGetCurrentPage() + 1,
      pageCount: Math.max(1, e.api.paginationGetTotalPages()),
      rowCount: e.api.paginationGetRowCount(),
    });
  }, []);
  const goToPage = useCallback(
    (n) => gridApi?.paginationGoToPage(n - 1),
    [gridApi],
  );
  const goFirst = useCallback(
    () => gridApi?.paginationGoToFirstPage(),
    [gridApi],
  );

  return {
    gridApi,
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  };
};

const ColumnText = ({ children }) => (
  <Box
    title={typeof children === "string" ? children : undefined}
    sx={{
      color: T.ink900,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {children}
  </Box>
);

// ── root: shared header + tabs + data ────────────────────────────────────────
export default function UsersScreen() {
  useDocumentTitle("Users");
  const { listAllUsers } = useUsersApi();
  const { activeSessions } = useActiveSessionsApi();
  const { listGroups } = useGroupsApi();
  const { enqueueSnackbar } = useSnackbar();

  // The tab lives in the URL (`/users/<slug>`) rather than in state, so the
  // header's Users dropdown can link to one and a tab can be shared or
  // bookmarked. Bare `/users` falls through to the first tab.
  const { tab } = useParams();
  const navigate = useNavigate();
  const view = viewFromSlug(tab);
  const setView = useCallback(
    (next) => navigate(usersTabPath(slugFromView(next))),
    [navigate],
  );
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [sessionsError, setSessionsError] = useState(null);
  const [groupsError, setGroupsError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await listAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setUsersError(
        e?.response?.data?.detail ??
          e?.message ??
          "Failed to load /users/all_users",
      );
    } finally {
      setUsersLoading(false);
    }
  }, [listAllUsers]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const res = await activeSessions();
      setSessions(Array.isArray(res?.sessions) ? res.sessions : []);
    } catch (e) {
      setSessionsError(
        e?.response?.data?.detail ?? e?.message ?? "Failed to load sessions",
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [activeSessions]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const data = await listGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      setGroupsError(
        e?.response?.data?.detail ?? e?.message ?? "Failed to load /groups",
      );
    } finally {
      setGroupsLoading(false);
    }
  }, [listGroups]);

  useEffect(() => {
    loadUsers();
    loadSessions();
    loadGroups();
  }, [loadUsers, loadSessions, loadGroups]);

  // Group memberships are needed by every view (the All Users / Active Sessions
  // "Groups" column and the Groups tab itself), so build the lookup once here.
  const groupsIndex = useMemo(() => buildUserGroupsIndex(groups), [groups]);

  // Whether THIS user is in the "User Activity" group — the only thing that
  // reveals the Activity tab. Resolved from /groups/for-user rather than the
  // `groups` list above, so it stays right even if that list is filtered.
  const { allowed: canSeeActivity } = useIsInUserActivityGroup();

  const onlineEmails = useMemo(
    () =>
      new Set(
        sessions.map((s) => (s.userEmail || "").toLowerCase()).filter(Boolean),
      ),
    [sessions],
  );

  // Built from the shared list so the in-page tabs and the header dropdown
  // can't drift apart; the counts and the live dot are this screen's to add.
  const tabCounts = {
    users: { count: users.length },
    sessions: { count: sessions.length, dot: true },
    groups: { count: groups.length },
  };
  const tabs = visibleUsersTabs({ canSeeActivity }).map((t) => ({
    value: t.value,
    label: t.label,
    ...(tabCounts[t.value] || {}),
  }));

  const refreshForView = {
    users: loadUsers,
    sessions: loadSessions,
    groups: loadGroups,
    // Activity fetches its own window and has its own refresh control.
    activity: () => {},
  };

  return (
    <PageShell>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: 23,
              fontWeight: 700,
              letterSpacing: "-.01em",
              color: T.ink900,
            }}
          >
            Users
          </Box>
          <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
            Everyone with portal access, and what they're doing right now.
          </Box>
        </Box>
        <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.25 }}
        >
          <HeaderActions
            onRefresh={refreshForView[view]}
            onAdd={() =>
              view === "groups" ? setCreateGroupOpen(true) : setCreateOpen(true)
            }
            addLabel={view === "groups" ? "Add Group" : "Add User"}
          />
        </Box>
      </Box>

      <Tabs value={view} onChange={setView} options={tabs} />

      {view === "users" && (
        <UsersView
          users={users}
          loading={usersLoading}
          error={usersError}
          onlineEmails={onlineEmails}
          groupsIndex={groupsIndex}
          groups={groups}
          onReload={loadUsers}
          onReloadGroups={loadGroups}
        />
      )}
      {view === "sessions" && (
        <ActiveSessionsView
          sessions={sessions}
          loading={sessionsLoading}
          error={sessionsError}
          groupsIndex={groupsIndex}
        />
      )}
      {view === "groups" && (
        <GroupsView
          groups={groups}
          loading={groupsLoading}
          error={groupsError}
          users={users}
          onReload={loadGroups}
        />
      )}
      {/* Gated twice on purpose: the tab is hidden AND the view refuses to
          render, so losing the group can't leave the last-selected tab live. */}
      {view === "activity" && canSeeActivity && <UserActivityView />}

      <SideDrawer
        show={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add User"
        drawerWidth="520px"
      >
        <CreateUserForm
          onDone={() => {
            setCreateOpen(false);
            loadUsers();
            enqueueSnackbar("User list refreshed", { variant: "default" });
          }}
        />
      </SideDrawer>

      <SideDrawer
        show={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        title="Add Group"
        drawerWidth="520px"
      >
        <GroupForm
          groups={groups}
          onDone={() => {
            setCreateGroupOpen(false);
            loadGroups();
          }}
        />
      </SideDrawer>
    </PageShell>
  );
}

// ── All Users ────────────────────────────────────────────────────────────────
function UsersView({
  users,
  loading,
  error,
  onlineEmails,
  groupsIndex,
  groups,
  onReload,
  onReloadGroups,
}) {
  const [filter, setFilter] = useState(ALL);
  const [search, setSearch] = useState("");
  const {
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  } = useGridPagination(15);

  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [activityUser, setActivityUser] = useState(null);
  const [jsonUser, setJsonUser] = useState(null);
  const [impUser, setImpUser] = useState(null);

  const isOnline = useCallback(
    (u) => onlineEmails.has((u.emailAddress || "").toLowerCase()),
    [onlineEmails],
  );

  const chips = useMemo(
    () => [
      { value: ALL, label: "All", count: users.length },
      ...roleChipsFrom(users),
    ],
    [users],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== ALL && !asArray(u.roles).includes(filter)) return false;
      if (q) {
        const hay =
          `${fullName(u)} ${u.emailAddress || ""} ${u.company_name || ""} ${asArray(u.roles).join(" ")} ${asArray(u.permissions).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, filter, search]);

  // New filter/search → jump back to the first page of the full result set.
  useEffect(() => goFirst(), [filter, search, goFirst]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "User",
        field: "emailAddress",
        flex: 1.8,
        minWidth: 250,
        cellStyle: centerCell,
        valueGetter: (p) => `${fullName(p.data)} ${p.data?.emailAddress || ""}`,
        cellRenderer: (p) => (
          <NameCell
            name={fullName(p.data)}
            email={p.data?.emailAddress}
            status={isOnline(p.data) ? "online" : "offline"}
          />
        ),
      },
      {
        headerName: "Roles",
        flex: 1,
        minWidth: 160,
        cellStyle: centerCell,
        valueGetter: (p) => asArray(p.data?.roles).map(roleLabel).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.roles).map(roleLabel).join(", ")}
            tags={asArray(p.data?.roles).map((r) => (
              <Tag
                key={r}
                label={roleLabel(r)}
                tone={r === "admin" ? "teal" : "slate"}
              />
            ))}
          />
        ),
      },
      {
        headerName: "Permissions",
        flex: 1,
        minWidth: 160,
        cellStyle: centerCell,
        valueGetter: (p) => asArray(p.data?.permissions).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.permissions).join(", ")}
            tags={asArray(p.data?.permissions).map((pm) => (
              <Tag key={pm} label={pm} tone="slate" />
            ))}
          />
        ),
      },
      {
        headerName: "Teams",
        flex: 1,
        minWidth: 130,
        cellStyle: centerCell,
        valueGetter: (p) => asArray(p.data?.teams).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.teams).join(", ")}
            tags={asArray(p.data?.teams).map((t) => (
              <Tag key={t} label={t} tone="purple" />
            ))}
          />
        ),
      },
      {
        headerName: "Groups",
        flex: 1,
        minWidth: 150,
        cellStyle: centerCell,
        valueGetter: (p) =>
          groupsForRow(groupsIndex, {
            id: userId(p.data),
            email: p.data?.emailAddress,
          }).join(", "),
        cellRenderer: (p) => {
          const names = groupsForRow(groupsIndex, {
            id: userId(p.data),
            email: p.data?.emailAddress,
          });
          return (
            <TagCell
              title={names.join(", ")}
              tags={names.map((g) => (
                <Tag key={g} label={g} tone="indigo" />
              ))}
            />
          );
        },
      },
      {
        headerName: "Company",
        field: "company_name",
        flex: 1,
        minWidth: 150,
        cellStyle: centerCell,
        cellRenderer: (p) => <ColumnText>{p.value || "—"}</ColumnText>,
      },
      {
        headerName: "Created",
        field: "createdDate",
        flex: 0.8,
        minWidth: 120,
        cellStyle: centerCell,
        sort: "desc",
        comparator: (a, b) => dateMs(a) - dateMs(b),
        valueFormatter: (p) =>
          p.value ? formatDateTime(p.value, "date") : "—",
      },
      {
        headerName: "Last Updated",
        field: "updateDateTime",
        flex: 0.9,
        minWidth: 130,
        cellStyle: centerCell,
        comparator: (a, b) => dateMs(a) - dateMs(b),
        cellRenderer: (p) =>
          p.value ? (
            <span title={formatDateTime(p.value)}>{timeAgo(p.value)}</span>
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              —
            </Box>
          ),
      },
      {
        // Pinned right, sized to exactly fit Edit + Details + More.
        headerName: "Actions",
        width: 218,
        minWidth: 218,
        flex: 0,
        pinned: "right",
        resizable: false,
        sortable: false,
        cellStyle: leftActions,
        cellRenderer: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ActionLink onClick={() => setEditUser(p.data)}>Edit</ActionLink>
            <ActionLink onClick={() => setJsonUser(p.data)}>Details</ActionLink>
            <RowActionsMenu
              label="More"
              items={[
                { label: "Impersonate", onClick: () => setImpUser(p.data) },
                { label: "Change Password", onClick: () => setPwUser(p.data) },
                {
                  label: "Activity",
                  onClick: () => setActivityUser(p.data),
                  divider: true,
                },
              ]}
            />
          </Box>
        ),
      },
    ],
    [isOnline, groupsIndex],
  );

  const total = filtered.length;
  const start = (pageInfo.page - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const summary = total ? `${start + 1}–${end} of ${total} users` : "0 users";

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <FilterChips value={filter} onChange={setFilter} options={chips} />
        <Box sx={{ ml: "auto" }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name, email, company, role…"
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <GridCard>
        {!loading && filtered.length === 0 ? (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              py: 7,
              px: 4,
            }}
          >
            <Box sx={{ fontSize: 17, fontWeight: 700, color: T.ink900 }}>
              No users match
            </Box>
            <Box sx={{ fontSize: 13.5, color: T.ink500, mt: 0.5 }}>
              Try a different filter or search term.
            </Box>
          </Box>
        ) : (
          <AgGridTableWrapper noBorder borderRadius="medium">
            <AgGridReact
              {...gridProps}
              rowData={filtered}
              columnDefs={columnDefs}
              getRowId={(p) => userId(p.data)}
              loading={loading && !users.length}
              paginationPageSize={pageSize}
              onGridReady={onGridReady}
              onPaginationChanged={onPaginationChanged}
            />
          </AgGridTableWrapper>
        )}
      </GridCard>

      <Footer
        summary={summary}
        page={pageInfo.page}
        pageCount={pageInfo.pageCount}
        pageSize={pageSize}
        onPage={goToPage}
        onPageSize={setPageSize}
      />

      <SideDrawer
        show={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        title="Edit User"
        subtitle={editUser ? fullName(editUser) : null}
        drawerWidth="520px"
      >
        {editUser && (
          <EditUserForm
            user={editUser}
            groups={groups}
            onGroupsChanged={onReloadGroups}
            onDone={() => {
              setEditUser(null);
              onReload();
            }}
          />
        )}
      </SideDrawer>

      <SideDrawer
        show={Boolean(activityUser)}
        onClose={() => setActivityUser(null)}
        title="Activity"
        subtitle={activityUser ? fullName(activityUser) : null}
        drawerWidth="560px"
      >
        {activityUser && <UserActivityPanel user={activityUser} />}
      </SideDrawer>

      <SideDrawer
        show={Boolean(jsonUser)}
        onClose={() => setJsonUser(null)}
        title="Details"
        subtitle={jsonUser?.emailAddress || null}
        drawerWidth="640px"
      >
        {jsonUser && <JsonPreview defaultCollapse={3} values={jsonUser} />}
      </SideDrawer>

      <SetPasswordDialog user={pwUser} onClose={() => setPwUser(null)} />
      <ImpersonateDialog user={impUser} onClose={() => setImpUser(null)} />
    </Box>
  );
}

// ── Active Sessions (grouped by user) ────────────────────────────────────────
function ActiveSessionsView({ sessions, loading, error, groupsIndex }) {
  const [filter, setFilter] = useState(ALL);
  const [search, setSearch] = useState("");
  const {
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  } = useGridPagination(15);
  const [activityUser, setActivityUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [jsonSession, setJsonSession] = useState(null);
  const [impUser, setImpUser] = useState(null);

  // One row per user; carry the most-recent session + the per-user session count.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      const key = s.userEmail || s.userId || s.sessionId;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return [...map.values()].map((rows) => {
      const latest = rows.reduce(
        (a, b) => (dateMs(b.lastActivityAt) > dateMs(a.lastActivityAt) ? b : a),
        rows[0],
      );
      const earliestExpiry = rows
        .map((r) => r.expiresAt)
        .filter(Boolean)
        .sort((a, b) => dateMs(a) - dateMs(b))[0];
      return { ...latest, sessionCount: rows.length, earliestExpiry };
    });
  }, [sessions]);

  const within1h = useCallback(
    (g) => Date.now() - dateMs(g.lastActivityAt) < 3600 * 1000,
    [],
  );
  const expiringSoon = useCallback((g) => {
    const e = g.earliestExpiry || g.expiresAt;
    return e && dateMs(e) - Date.now() < 24 * 3600 * 1000;
  }, []);

  const chips = useMemo(
    () => [
      { value: ALL, label: "All sessions", count: sessions.length },
      {
        value: "__active1h__",
        label: "Active < 1 hr",
        count: grouped.filter(within1h).length,
      },
      ...roleChipsFrom(grouped),
      {
        value: "__multi__",
        label: "Multiple sessions",
        count: grouped.filter((g) => g.sessionCount > 1).length,
      },
      {
        value: "__expiring__",
        label: "Expiring < 24 hrs",
        count: grouped.filter(expiringSoon).length,
      },
    ],
    [sessions, grouped, within1h, expiringSoon],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const roleFilters = new Set([
      "__active1h__",
      "__multi__",
      "__expiring__",
      ALL,
    ]);
    return grouped.filter((g) => {
      if (filter === "__active1h__" && !within1h(g)) return false;
      else if (filter === "__multi__" && g.sessionCount <= 1) return false;
      else if (filter === "__expiring__" && !expiringSoon(g)) return false;
      else if (!roleFilters.has(filter) && !asArray(g.roles).includes(filter))
        return false;
      if (q) {
        const hay =
          `${g.name || ""} ${g.userEmail || ""} ${g.ipAddress || ""} ${asArray(g.roles).join(" ")} ${parseUA(g.userAgent)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [grouped, filter, search, within1h, expiringSoon]);

  // New filter/search → jump back to the first page of the full result set.
  useEffect(() => goFirst(), [filter, search, goFirst]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "User",
        field: "userEmail",
        flex: 1.6,
        minWidth: 240,
        cellStyle: centerCell,
        valueGetter: (p) => `${p.data?.name || ""} ${p.data?.userEmail || ""}`,
        cellRenderer: (p) => (
          <NameCell
            name={p.data?.name || p.data?.userEmail || "—"}
            email={p.data?.userEmail}
            status="online"
          />
        ),
      },
      {
        headerName: "Roles",
        flex: 1,
        minWidth: 150,
        cellStyle: centerCell,
        valueGetter: (p) => asArray(p.data?.roles).map(roleLabel).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.roles).map(roleLabel).join(", ")}
            tags={asArray(p.data?.roles).map((r) => (
              <Tag
                key={r}
                label={roleLabel(r)}
                tone={r === "admin" ? "teal" : "slate"}
              />
            ))}
          />
        ),
      },
      {
        headerName: "Groups",
        flex: 1,
        minWidth: 140,
        cellStyle: centerCell,
        valueGetter: (p) =>
          groupsForRow(groupsIndex, {
            id: p.data?.userId,
            email: p.data?.userEmail,
          }).join(", "),
        cellRenderer: (p) => {
          const names = groupsForRow(groupsIndex, {
            id: p.data?.userId,
            email: p.data?.userEmail,
          });
          return (
            <TagCell
              title={names.join(", ")}
              tags={names.map((g) => (
                <Tag key={g} label={g} tone="indigo" />
              ))}
            />
          );
        },
      },
      {
        headerName: "Sessions",
        field: "sessionCount",
        flex: 0.7,
        minWidth: 110,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          (p.value || 1) > 1 ? (
            <Tag label={`${p.value} sessions`} tone="teal" />
          ) : (
            <Box component="span" sx={{ color: T.ink500, fontSize: 13 }}>
              1 session
            </Box>
          ),
      },
      {
        headerName: "Last Activity",
        field: "lastActivityAt",
        flex: 1,
        minWidth: 140,
        sort: "desc",
        cellStyle: centerCell,
        comparator: (a, b) => dateMs(a) - dateMs(b),
        cellRenderer: (p) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              lineHeight: 1.3,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                color: T.ink900,
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={p.value ? formatDateTime(p.value) : ""}
            >
              {timeAgo(p.value)}
            </Box>
          </Box>
        ),
      },
      {
        headerName: "Last Event",
        field: "lastEvent",
        flex: 0.8,
        minWidth: 130,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          p.value ? (
            <Tag label={p.value} tone={eventTone(p.value)} />
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              —
            </Box>
          ),
      },
      {
        headerName: "Device / IP",
        field: "userAgent",
        flex: 1.3,
        minWidth: 180,
        cellStyle: centerCell,
        valueGetter: (p) =>
          `${parseUA(p.data?.userAgent)} ${p.data?.ipAddress || ""}`,
        cellRenderer: (p) => (
          <Box
            title={`${parseUA(p.data?.userAgent) || ""}${
              p.data?.ipAddress ? ` · ${p.data.ipAddress}` : ""
            }`}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              lineHeight: 1.35,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: T.ink900,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {parseUA(p.data?.userAgent) || "—"}
              </Box>
              {p.data?.clientId && <Tag label={p.data.clientId} tone="slate" />}
            </Box>
            {p.data?.ipAddress && (
              <Box
                sx={{
                  fontSize: 12,
                  color: T.ink500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.data.ipAddress}
              </Box>
            )}
          </Box>
        ),
      },
      {
        headerName: "Expires",
        field: "expiresAt",
        flex: 0.9,
        minWidth: 130,
        cellStyle: centerCell,
        comparator: (a, b) => dateMs(a) - dateMs(b),
        cellRenderer: (p) => {
          const multi = (p.data?.sessionCount || 1) > 1;
          const d = multi ? p.data?.earliestExpiry : p.value;
          const line = {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          };
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                lineHeight: 1.35,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Box sx={{ ...line, color: T.ink900, fontWeight: 600 }}>
                {expiresLabel(d) || "—"}
              </Box>
              {d && (
                <Box sx={{ ...line, fontSize: 12, color: T.ink500 }}>
                  {multi ? "earliest " : ""}
                  {formatDateTime(d, "date")}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        // Same convention as the Users grid: pinned right, sized to its control.
        headerName: "Actions",
        width: 84,
        minWidth: 84,
        flex: 0,
        pinned: "right",
        resizable: false,
        sortable: false,
        cellStyle: leftActions,
        cellRenderer: (p) => {
          const u = sessionToUser(p.data);
          return (
            <RowActionsMenu
              items={[
                { label: "Activity", onClick: () => setActivityUser(u) },
                { label: "Change Password", onClick: () => setPwUser(u) },
                { label: "Impersonate", onClick: () => setImpUser(u) },
                {
                  label: "Details",
                  onClick: () => setJsonSession(p.data),
                  divider: true,
                },
              ]}
            />
          );
        },
      },
    ],
    [groupsIndex],
  );

  const summary = `${grouped.length} ${grouped.length === 1 ? "user" : "users"} · ${sessions.length} open ${sessions.length === 1 ? "session" : "sessions"}`;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <FilterChips value={filter} onChange={setFilter} options={chips} />
        <Box sx={{ ml: "auto" }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name, email, IP, role…"
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <GridCard>
        {!loading && filtered.length === 0 ? (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              py: 7,
              px: 4,
            }}
          >
            <Box sx={{ fontSize: 17, fontWeight: 700, color: T.ink900 }}>
              No active sessions
            </Box>
            <Box sx={{ fontSize: 13.5, color: T.ink500, mt: 0.5 }}>
              No one is currently signed in (or the session window has lapsed).
            </Box>
          </Box>
        ) : (
          <AgGridTableWrapper noBorder borderRadius="medium">
            <AgGridReact
              {...gridProps}
              rowData={filtered}
              columnDefs={columnDefs}
              getRowId={(p) =>
                String(
                  p.data?.userId ??
                    p.data?.userEmail ??
                    p.data?.sessionId ??
                    "",
                )
              }
              loading={loading && !sessions.length}
              paginationPageSize={pageSize}
              onGridReady={onGridReady}
              onPaginationChanged={onPaginationChanged}
            />
          </AgGridTableWrapper>
        )}
      </GridCard>

      <Footer
        summary={summary}
        page={pageInfo.page}
        pageCount={pageInfo.pageCount}
        pageSize={pageSize}
        onPage={goToPage}
        onPageSize={setPageSize}
      />

      <SideDrawer
        show={Boolean(activityUser)}
        onClose={() => setActivityUser(null)}
        title="Activity"
        subtitle={activityUser ? fullName(activityUser) : null}
        drawerWidth="560px"
      >
        {activityUser && <UserActivityPanel user={activityUser} />}
      </SideDrawer>

      <SideDrawer
        show={Boolean(jsonSession)}
        onClose={() => setJsonSession(null)}
        title="Session Details"
        subtitle={jsonSession?.userEmail || null}
        drawerWidth="640px"
      >
        {jsonSession && (
          <JsonPreview defaultCollapse={3} values={jsonSession} />
        )}
      </SideDrawer>

      <SetPasswordDialog user={pwUser} onClose={() => setPwUser(null)} />
      <ImpersonateDialog user={impUser} onClose={() => setImpUser(null)} />
    </Box>
  );
}

/* ── Form primitives ─────────────────────────────────────────────────────────
   Every input on this screen is label-ABOVE-control (the house rule — see
   CLAUDE.md "Form inputs"): wrap the control in InputWrapper and never pass
   `label` to the TextField/Select, which is what suppresses MUI's notch. */
const UField = ({ label, helperText, required, children }) => (
  <InputWrapper
    fullWidth
    label={label}
    helperText={helperText}
    required={required}
    labelSx={{ fontSize: 12.5, fontWeight: 600, color: T.ink900 }}
  >
    {children}
  </InputWrapper>
);

// Role option row: the friendly label, with the raw stored value beside it in a
// smaller muted mono face — admins pick "Junior Underwriter" but need to see
// that what lands on the user doc is `preUnderwriter`.
const renderRoleOption = ({ key, ...props }, option) => (
  <Box
    component="li"
    key={key ?? option}
    {...props}
    // `&&` doubles the specificity so it beats MUI's own
    // `.MuiAutocomplete-option { align-items: center }` descendant rule.
    sx={{ "&&": { display: "flex", alignItems: "baseline", gap: 1 } }}
  >
    <Box component="span" sx={{ fontSize: 13.5, color: T.ink900 }}>
      {roleLabel(option)}
    </Box>
    {roleLabel(option) !== option && (
      <Box
        component="span"
        sx={{
          fontSize: 11,
          color: T.ink500,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {option}
      </Box>
    )}
  </Box>
);

// Distinct, trimmed union of `base` (kept first, in its own order) and every
// value in `lists`, extras sorted case-insensitively. Seeds the group-form
// pickers from what's already applied — OrgGroup.departments / relatedRoles
// carry no backend enum, so "in use" is the only source of options.
const collectOptions = (lists, base = []) => {
  const seen = new Set(base);
  const extra = [];
  for (const v of lists.flat()) {
    const s = typeof v === "string" ? v.trim() : "";
    if (s && !seen.has(s)) {
      seen.add(s);
      extra.push(s);
    }
  }
  extra.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return [...base, ...extra];
};

// Multi-select with a checkbox per option that also takes free-typed values
// (Enter adds; trimmed, blanks and duplicates dropped).
function ChecklistAutocomplete({ options, value, onChange }) {
  return (
    <Autocomplete
      multiple
      freeSolo
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, v) => onChange(collectOptions([v]))}
      renderOption={({ key, ...props }, option, { selected }) => (
        <Box
          component="li"
          key={key ?? option}
          {...props}
          sx={{ "&&": { display: "flex", alignItems: "center", gap: 0.5 } }}
        >
          <Checkbox size="small" checked={selected} sx={{ p: 0.5, mr: 0.5 }} />
          <Box component="span" sx={{ fontSize: 13.5, color: T.ink900 }}>
            {option}
          </Box>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} />}
    />
  );
}

// `teams` is a real permission bucket, not decoration: user_in_group(user,
// 'capital_markets', field='teams') gates lock request/confirm in the pricing
// router (PERMISSION_GROUP_FIELDS = roles | permissions | teams).
const TEAM_OPTIONS = ["capital_markets"];

// The permission strings the app actually checks (usePermissionsHook) plus the
// backend's B2BUser.PIPELINE_ACCESS_ROLES set. freeSolo — the list is a
// convenience, not an enum.
const PERMISSION_OPTIONS = [
  "admin",
  "underwriting",
  "operations",
  "sales_admin",
];

// UserGroup buckets defined in app/models/users_b2b.py (each maps to a default
// profile-permission set on the backend).
const USER_GROUP_OPTIONS = [
  "loan_officer",
  "underwriter",
  "operations",
  "admin",
];

const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "Asia/Kolkata",
  "UTC",
];

// ── Create user (POST /users/create_user) ────────────────────────────────────
function CreateUserForm({ onDone }) {
  const { createUser } = useUsersApi();
  const { list } = useCorrespondentsApi();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phone: "",
    org_name: "",
  });
  const [correspondent, setCorrespondent] = useState(null);
  const [correspondents, setCorrespondents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    list()
      .then((rows) => setCorrespondents(Array.isArray(rows) ? rows : []))
      .catch(() => setCorrespondents([]));
  }, [list]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createUser({
        firstName: form.firstName,
        lastName: form.lastName,
        emailAddress: form.emailAddress,
        phone: form.phone || undefined,
        org_name: correspondent?.companyName || form.org_name || undefined,
        correspondentCompanyId: correspondent
          ? (correspondent._id ?? correspondent.id)
          : undefined,
      });
      enqueueSnackbar(`User ${form.emailAddress} created`, {
        variant: "success",
      });
      onDone();
    } catch (err) {
      setError(
        err?.response?.data?.detail ??
          err?.message ??
          "Create failed (the email domain may be disallowed)",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        <UField label="First Name" required>
          <TextField
            value={form.firstName}
            onChange={set("firstName")}
            fullWidth
            required
          />
        </UField>
        <UField label="Last Name" required>
          <TextField
            value={form.lastName}
            onChange={set("lastName")}
            fullWidth
            required
          />
        </UField>
        <UField label="Email" required>
          <TextField
            type="email"
            value={form.emailAddress}
            onChange={set("emailAddress")}
            fullWidth
            required
          />
        </UField>
        <UField label="Phone">
          <TextField value={form.phone} onChange={set("phone")} fullWidth />
        </UField>
        <UField
          label="Correspondent (optional)"
          helperText="Sets correspondentCompanyId + org_name. Leave blank for internal."
        >
          <Autocomplete
            options={correspondents}
            value={correspondent}
            onChange={(_, v) => setCorrespondent(v)}
            getOptionLabel={(o) => o?.companyName || ""}
            isOptionEqualToValue={(o, v) =>
              (o._id ?? o.id) === (v?._id ?? v?.id)
            }
            renderInput={(params) => <TextField {...params} />}
          />
        </UField>
        {!correspondent && (
          <UField
            label="Org name (originator)"
            helperText="Used as company_name / loan originator when no correspondent is chosen."
          >
            <TextField
              value={form.org_name}
              onChange={set("org_name")}
              fullWidth
            />
          </UField>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={tealBtnSx}
          >
            {submitting ? "Creating…" : "Create user"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

/* ── Edit user (POST /users/update_user_by_id/:id) ────────────────────────────
   The endpoint is a raw `$set` of whatever body it receives
   (B2BUser.update_backend_user), so every field on the user doc is writable —
   there is no server-side whitelist. What's exposed here is the set that
   actually drives behaviour somewhere:

     roles        — display/assignment buckets (see ROLE_OPTIONS)
     permissions  — usePermissionsHook (admin / underwriting) + the backend's
                    B2BUser.PIPELINE_ACCESS_ROLES
     teams        — user_in_group(user, 'capital_markets', field='teams') gates
                    lock request/confirm in the pricing router
     user_group   — the UserGroup profile-permission bucket
     user_title   — display title (roles were mass-defaulted and are unreliable)
     email_aliases— inbound email matching + group-member resolution
     timezone     — per-user timestamp rendering

   Group membership is NOT part of this payload: it lives in the org_groups
   collection and is written through /groups/:id/members, so it's diffed and
   applied separately below (the backend mirrors it onto user.groups). */
function EditUserForm({ user, groups = [], onDone, onGroupsChanged }) {
  const { updateUser } = useUsersApi();
  const { addMember, removeMember } = useGroupsApi();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phone_number: user.phone_number || "",
    company_name: user.company_name || "",
    user_title: user.user_title || "",
    user_group: user.user_group || "",
    timezone: user.timezone || "",
  });
  const [roles, setRoles] = useState(asArray(user.roles));
  const [permissions, setPermissions] = useState(asArray(user.permissions));
  const [teams, setTeams] = useState(asArray(user.teams));
  const [aliases, setAliases] = useState(asArray(user.email_aliases));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Group names this user currently belongs to, resolved by _id OR email (a
  // member row can carry either — see buildUserGroupsIndex).
  const initialGroups = useMemo(() => {
    const id = userId(user);
    const email = (user.emailAddress || "").toLowerCase();
    return groups
      .filter((g) =>
        (g.members || []).some(
          (m) =>
            (m.userId && String(m.userId) === id) ||
            (m.email || "").toLowerCase() === email,
        ),
      )
      .map((g) => g.name);
  }, [groups, user]);
  const [groupNames, setGroupNames] = useState(initialGroups);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await updateUser(userId(user), {
        ...form,
        roles,
        permissions,
        teams,
        email_aliases: aliases,
      });

      const added = groupNames.filter((n) => !initialGroups.includes(n));
      const removed = initialGroups.filter((n) => !groupNames.includes(n));
      if (added.length || removed.length) {
        const idFor = (name) => {
          const g = groups.find((x) => x.name === name);
          return String(g?._id ?? g?.oid ?? name);
        };
        for (const name of added)
          await addMember(idFor(name), user.emailAddress, fullName(user));
        for (const name of removed)
          await removeMember(idFor(name), user.emailAddress);
        onGroupsChanged?.();
      }

      enqueueSnackbar("User updated", { variant: "success" });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Update failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        <UField
          label="Email"
          helperText="Email is the identity key and isn't editable here."
        >
          <TextField value={user.emailAddress || ""} fullWidth disabled />
        </UField>
        <UField label="First Name">
          <TextField
            value={form.firstName}
            onChange={set("firstName")}
            fullWidth
          />
        </UField>
        <UField label="Last Name">
          <TextField
            value={form.lastName}
            onChange={set("lastName")}
            fullWidth
          />
        </UField>
        <UField label="Phone">
          <TextField
            value={form.phone_number}
            onChange={set("phone_number")}
            fullWidth
          />
        </UField>
        <UField label="Company">
          <TextField
            value={form.company_name}
            onChange={set("company_name")}
            fullWidth
          />
        </UField>
        <UField
          label="Title"
          helperText="Display title shown across the portal (roles are unreliable for display)."
        >
          <TextField
            value={form.user_title}
            onChange={set("user_title")}
            fullWidth
            placeholder="e.g. Senior Underwriter"
          />
        </UField>
        <UField
          label="User Group"
          helperText="Profile-permission bucket applied on the backend."
        >
          <TextField
            select
            value={form.user_group}
            onChange={set("user_group")}
            fullWidth
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {USER_GROUP_OPTIONS.map((g) => (
              <MenuItem key={g} value={g}>
                {roleLabel(g)}
              </MenuItem>
            ))}
          </TextField>
        </UField>
        <UField label="Roles">
          <Autocomplete
            multiple
            freeSolo
            options={ROLE_OPTIONS}
            getOptionLabel={roleLabel}
            renderOption={renderRoleOption}
            value={roles}
            onChange={(_, v) => setRoles(v)}
            renderInput={(params) => <TextField {...params} />}
          />
        </UField>
        <UField
          label="Permissions"
          helperText="Access gates — `admin` and `underwriting` unlock admin screens and underwriting actions."
        >
          <Autocomplete
            multiple
            freeSolo
            options={PERMISSION_OPTIONS}
            value={permissions}
            onChange={(_, v) => setPermissions(v)}
            renderInput={(params) => <TextField {...params} />}
          />
        </UField>
        <UField
          label="Teams"
          helperText="`capital_markets` grants the lock desk (request / confirm rate locks)."
        >
          <Autocomplete
            multiple
            freeSolo
            options={TEAM_OPTIONS}
            value={teams}
            onChange={(_, v) => setTeams(v)}
            renderInput={(params) => <TextField {...params} />}
          />
        </UField>
        <UField
          label="Groups"
          helperText="Org groups (Opening Team, Pre-UW Team, QC Team…). Saved through /groups membership, admin only."
        >
          <Autocomplete
            multiple
            options={groups.map((g) => g.name).filter(Boolean)}
            value={groupNames}
            onChange={(_, v) => setGroupNames(v)}
            renderInput={(params) => <TextField {...params} />}
          />
        </UField>
        <UField
          label="Email Aliases"
          helperText="Other addresses this user sends from — lets inbound email and group imports match them."
        >
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={aliases}
            onChange={(_, v) => setAliases(v)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Press Enter to add" />
            )}
          />
        </UField>
        <UField label="Timezone">
          <TextField
            select
            value={form.timezone}
            onChange={set("timezone")}
            fullWidth
          >
            <MenuItem value="">
              <em>Default (America/New_York)</em>
            </MenuItem>
            {TIMEZONE_OPTIONS.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </TextField>
        </UField>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={tealBtnSx}
          >
            {submitting ? "Saving…" : "Save changes"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// ── Set password (POST /auth/admin/users/:id/set-password) ───────────────────
function SetPasswordDialog({ user, onClose }) {
  const { setPassword } = useAdminUsersApi();
  const { enqueueSnackbar } = useSnackbar();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setPw("");
      setConfirm("");
      setError(null);
    }
  }, [user]);

  async function submit() {
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setPassword(userId(user), pw);
      enqueueSnackbar(
        `Password set for ${user.emailAddress} (their sessions were revoked).`,
        { variant: "success" },
      );
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.detail ?? e?.message ?? "Failed to set password",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={Boolean(user)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{ fontSize: 18, fontWeight: 700, color: T.ink900, pb: 1 }}
      >
        Set password
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: 13, color: T.ink700, mb: 2 }}>
          Sets a new password for <strong>{user?.emailAddress}</strong> and
          revokes their live sessions (forces re-login).
        </Box>
        <Stack spacing={2}>
          <UField label="New password">
            <TextField
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              fullWidth
              autoFocus
            />
          </UField>
          <UField label="Confirm password">
            <TextField
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              fullWidth
            />
          </UField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !pw}
          sx={tealBtnSx}
        >
          {submitting ? "Setting…" : "Set password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Impersonate (POST /auth/admin/impersonate) — confirm + reason ────────────
// Explains the eastview-os behavior before issuing the act-as session.
function ImpersonateDialog({ user, onClose }) {
  const { impersonate } = useAdminUsersApi();
  const { startImpersonation } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setReason("");
      setError(null);
    }
  }, [user]);

  async function confirm() {
    if (!reason.trim()) {
      setError("A reason is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await impersonate(userId(user), reason.trim());
      // Apply the issued act-as token so this session actually switches to the
      // target user; AuthProvider stores it (sessionStorage, takes precedence)
      // and re-fetches /auth/me, flipping isImpersonating → true.
      const token = res?.access_token ?? res?.session_token;
      if (token) await startImpersonation(token);
      enqueueSnackbar(`Now impersonating ${user.emailAddress}.`, {
        variant: "success",
      });
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.detail ?? e?.message ?? "Impersonation failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const li = { fontSize: 13, color: T.ink700, mb: 0.75, lineHeight: 1.5 };

  return (
    <Dialog open={Boolean(user)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ fontSize: 18, fontWeight: 700, color: T.ink900, pb: 1 }}
      >
        Impersonate {user?.emailAddress}?
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: 13.5, color: T.ink700, mb: 1.5 }}>
          This opens a short-lived <strong>act-as session</strong> as this user
          (admin sudo). Per eastview-os:
        </Box>
        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
          <Box component="li" sx={li}>
            Issues a session as <strong>{user?.emailAddress}</strong> that
            expires in <strong>30 minutes</strong> (capped at 60).
          </Box>
          <Box component="li" sx={li}>
            The user is <strong>emailed</strong> that an administrator signed in
            as them.
          </Box>
          <Box component="li" sx={li}>
            The action is <strong>audited</strong> under your admin identity
            with the reason below.
          </Box>
          <Box component="li" sx={li}>
            Irreversible actions may be blocked while impersonating.
          </Box>
        </Box>
        <Box sx={{ fontSize: 12, color: T.ink500, mb: 2, fontStyle: "italic" }}>
          Note: this portal issues the token but doesn't switch your current
          session into it.
        </Box>
        <UField label="Reason" required>
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            autoFocus
            multiline
            minRows={2}
            placeholder="e.g. Troubleshooting a loan the user reported"
          />
        </UField>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={submitting || !reason.trim()}
          sx={tealBtnSx}
        >
          {submitting ? "Working…" : "Confirm & impersonate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const MiniStat = ({ label, value, sub }) => (
  <Box
    sx={{
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      background: "#fbfdfe",
      px: 1.5,
      py: 1.1,
    }}
  >
    <Box
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: T.ink500,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: 17,
        fontWeight: 800,
        color: T.ink900,
        mt: "2px",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Box>
    {sub && <Box sx={{ fontSize: 11, color: T.ink500, mt: "1px" }}>{sub}</Box>}
  </Box>
);

// ── Per-user activity (GET /auth/admin/users/:id/activity) ───────────────────
function UserActivityPanel({ user }) {
  const { userActivity } = useAdminUsersApi();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    userActivity(userId(user))
      .then((data) => alive && setRows(Array.isArray(data) ? data : []))
      .catch(
        (e) =>
          alive &&
          setError(e?.response?.data?.detail ?? "Failed to load activity"),
      );
    return () => {
      alive = false;
    };
  }, [user, userActivity]);

  const stats = useMemo(() => {
    if (!rows?.length) return null;
    const sorted = [...rows].sort((a, b) => dateMs(b.at) - dateMs(a.at));
    const lastLogin = sorted.find(
      (r) => r.event === "login" && (!r.outcome || r.outcome === "success"),
    );
    return {
      total: rows.length,
      lastActivity: sorted[0]?.at,
      lastLogin: lastLogin?.at,
      failed: rows.filter(
        (r) =>
          r.event === "failed_login" || (r.outcome && r.outcome !== "success"),
      ).length,
      lastIp: sorted.find((r) => r.ip_address)?.ip_address,
      lastAuth: sorted.find((r) => r.auth_method)?.auth_method,
    };
  }, [rows]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (rows === null)
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  if (!rows.length)
    return <Box sx={{ color: T.ink500 }}>No recorded activity.</Box>;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <MiniStat label="Total events" value={stats.total} />
        <MiniStat label="Failed logins" value={stats.failed} />
        <MiniStat
          label="Last login"
          value={
            stats.lastLogin ? formatDateTime(stats.lastLogin, "date") : "—"
          }
          sub={stats.lastLogin ? timeAgo(stats.lastLogin) : undefined}
        />
        <MiniStat
          label="Last activity"
          value={stats.lastActivity ? timeAgo(stats.lastActivity) : "—"}
          sub={
            stats.lastActivity ? formatDateTime(stats.lastActivity) : undefined
          }
        />
      </Box>
      {(stats.lastIp || stats.lastAuth) && (
        <Box sx={{ fontSize: 12, color: T.ink500 }}>
          {stats.lastIp ? `Last IP ${stats.lastIp}` : ""}
          {stats.lastIp && stats.lastAuth ? " · " : ""}
          {stats.lastAuth ? `via ${stats.lastAuth}` : ""}
        </Box>
      )}
      <Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: T.ink500,
            mb: 1,
          }}
        >
          Recent events
        </Box>
        <Stack spacing={1.25}>
          {rows.map((r, i) => {
            const bad = r.outcome && r.outcome !== "success";
            return (
              <Box
                key={r._id ?? i}
                sx={{
                  border: `1px solid ${T.line}`,
                  borderRadius: T.radius,
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tag
                      label={r.event}
                      tone={bad ? "red" : eventTone(r.event)}
                    />
                    {bad && (
                      <Box sx={{ fontSize: 12, color: T.red }}>{r.outcome}</Box>
                    )}
                  </Box>
                  <Box
                    sx={{ fontSize: 12, color: T.ink500 }}
                    title={r.at ? formatDateTime(r.at) : ""}
                  >
                    {timeAgo(r.at)}
                  </Box>
                </Box>
                {(r.request_path || r.ip_address || r.auth_method) && (
                  <Box sx={{ fontSize: 11.5, color: T.ink500, mt: 0.75 }}>
                    {r.request_method ? `${r.request_method} ` : ""}
                    {r.request_path || ""}
                    {r.ip_address ? ` · ${r.ip_address}` : ""}
                    {r.auth_method ? ` · ${r.auth_method}` : ""}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}

// ── Groups (org RBAC) ────────────────────────────────────────────────────────
// Surfaces eastview-os's /groups router: list/create/update groups and add/remove
// members. Groups are membership buckets carrying business-unit / departments /
// related-roles / portal-activity metadata; the same data feeds the "Groups"
// column on the All Users + Active Sessions grids above.
const groupKey = (g) => String(g?._id ?? g?.oid ?? g?.name ?? "");

const GroupNameCell = ({ name, description }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
      minWidth: 0,
      gap: "2px",
    }}
  >
    <Box
      sx={{
        fontWeight: 600,
        fontSize: 13.5,
        lineHeight: 1.2,
        color: T.ink900,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </Box>
    {description && (
      // Wraps instead of running past the cell; clamped to 2 lines so it can't
      // outgrow the fixed row height. Full text stays in the native tooltip.
      <Box
        sx={{
          fontSize: 12,
          lineHeight: 1.25,
          color: T.ink500,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
        title={description}
      >
        {description}
      </Box>
    )}
  </Box>
);

// Hover contents for the Members chip — name over email, one row per member.
// Long rosters are capped so the tooltip can't run off-screen.
const MEMBER_TOOLTIP_LIMIT = 15;

const MemberListTooltip = ({ members }) => {
  const shown = members.slice(0, MEMBER_TOOLTIP_LIMIT);
  const extra = members.length - shown.length;
  return (
    <Box sx={{ py: 0.25 }}>
      {shown.map((m, i) => (
        <Box
          key={m.email || m.userId || i}
          sx={{ mb: i === shown.length - 1 ? 0 : 0.75 }}
        >
          {m.fullName && (
            <Box sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>
              {m.fullName}
            </Box>
          )}
          <Box sx={{ fontSize: 12, lineHeight: 1.3, opacity: 0.85 }}>
            {m.email || "—"}
          </Box>
        </Box>
      ))}
      {extra > 0 && (
        <Box sx={{ fontSize: 12, mt: 0.75, opacity: 0.75 }}>+{extra} more</Box>
      )}
    </Box>
  );
};

function GroupsView({ groups, loading, error, users = [], onReload }) {
  const [filter, setFilter] = useState(ALL);
  const [search, setSearch] = useState("");
  const {
    onGridReady,
    onPaginationChanged,
    pageSize,
    setPageSize,
    pageInfo,
    goToPage,
    goFirst,
  } = useGridPagination(15);

  const [editGroup, setEditGroup] = useState(null);
  const [membersGroup, setMembersGroup] = useState(null);
  const [jsonGroup, setJsonGroup] = useState(null);

  // Filter chips by business unit (the natural grouping in the source data).
  //
  // A group with no business unit gets NO chip. "—" is the absence of a value,
  // not a category to filter by, and a chip labelled with a dash reads as a
  // broken one. Those groups still show under All and still match search — they
  // just aren't a bucket of their own. (So the chip counts don't sum to the
  // total, by design.)
  const chips = useMemo(() => {
    const counts = {};
    for (const g of groups) {
      const bu = String(g.businessUnit || "").trim();
      if (!bu) continue;
      counts[bu] = (counts[bu] || 0) + 1;
    }
    return [
      { value: ALL, label: "All", count: groups.length },
      ...Object.keys(counts)
        .sort()
        .map((bu) => ({ value: bu, label: bu, count: counts[bu] })),
    ];
  }, [groups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (filter !== ALL && String(g.businessUnit || "").trim() !== filter)
        return false;
      if (q) {
        const hay =
          `${g.name || ""} ${g.description || ""} ${g.businessUnit || ""} ${asArray(g.departments).join(" ")} ${asArray(g.relatedRoles).join(" ")} ${g.portalActivities || ""} ${g.relatedVendors || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [groups, filter, search]);

  useEffect(() => goFirst(), [filter, search, goFirst]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Group",
        field: "name",
        flex: 1.6,
        minWidth: 220,
        cellStyle: centerCell,
        valueGetter: (p) =>
          `${p.data?.name || ""} ${p.data?.description || ""}`,
        cellRenderer: (p) => (
          <GroupNameCell
            name={p.data?.name}
            description={p.data?.description}
          />
        ),
      },
      {
        headerName: "Members",
        field: "memberCount",
        flex: 0.6,
        minWidth: 120,
        cellStyle: centerCell,
        valueGetter: (p) =>
          p.data?.memberCount ?? asArray(p.data?.members).length,
        cellRenderer: (p) => {
          const list = asArray(p.data?.members);
          const n = p.data?.memberCount ?? list.length;
          const chip = (
            <Tag
              label={`${n} member${n === 1 ? "" : "s"}`}
              tone={n ? "indigo" : "slate"}
            />
          );
          if (!list.length) return chip;
          return (
            <Tooltip
              arrow
              placement="top"
              // The tooltip portals to <body>, so it escapes the cell's overflow.
              slotProps={{ tooltip: { sx: { maxWidth: 380 } } }}
              title={<MemberListTooltip members={list} />}
            >
              <Box
                component="span"
                sx={{ display: "inline-flex", cursor: "default" }}
              >
                {chip}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Business Unit",
        field: "businessUnit",
        flex: 0.8,
        minWidth: 130,
        cellStyle: centerCell,
        cellRenderer: (p) =>
          p.value ? (
            <Tag label={p.value} tone="teal" />
          ) : (
            <Box component="span" sx={{ color: T.ink300 }}>
              —
            </Box>
          ),
      },
      {
        headerName: "Departments",
        flex: 0.8,
        minWidth: 130,
        cellStyle: centerCell,
        valueGetter: (p) => asArray(p.data?.departments).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.departments).join(", ")}
            tags={asArray(p.data?.departments).map((d) => (
              <Tag key={d} label={d} tone="slate" />
            ))}
          />
        ),
      },
      {
        // Widest of the tag columns — a group can carry several role tags and
        // they were clipping at 0.8 flex.
        headerName: "Related Roles",
        flex: 1.6,
        minWidth: 200,
        cellStyle: centerCell,
        valueGetter: (p) =>
          asArray(p.data?.relatedRoles).map(roleLabel).join(", "),
        cellRenderer: (p) => (
          <TagCell
            title={asArray(p.data?.relatedRoles).map(roleLabel).join(", ")}
            tags={asArray(p.data?.relatedRoles).map((r) => (
              <Tag key={r} label={roleLabel(r)} tone="purple" />
            ))}
          />
        ),
      },
      {
        // Pinned right, sized to exactly fit its controls (no flex) so the
        // buttons stay reachable however the rest of the grid scrolls.
        headerName: "Actions",
        width: 202,
        minWidth: 202,
        flex: 0,
        pinned: "right",
        resizable: false,
        sortable: false,
        cellStyle: leftActions,
        cellRenderer: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ActionLink onClick={() => setMembersGroup(p.data)}>
              Members
            </ActionLink>
            <ActionLink onClick={() => setEditGroup(p.data)}>Edit</ActionLink>
            <RowActionsMenu
              items={[
                { label: "Details", onClick: () => setJsonGroup(p.data) },
              ]}
            />
          </Box>
        ),
      },
    ],
    [],
  );

  const total = filtered.length;
  const start = (pageInfo.page - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const summary = total ? `${start + 1}–${end} of ${total} groups` : "0 groups";

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <FilterChips value={filter} onChange={setFilter} options={chips} />
        <Box sx={{ ml: "auto" }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name, unit, department, role…"
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <GridCard>
        {!loading && filtered.length === 0 ? (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              py: 7,
              px: 4,
            }}
          >
            <Box sx={{ fontSize: 17, fontWeight: 700, color: T.ink900 }}>
              No groups match
            </Box>
            <Box sx={{ fontSize: 13.5, color: T.ink500, mt: 0.5 }}>
              Try a different filter, or add a group.
            </Box>
          </Box>
        ) : (
          <AgGridTableWrapper noBorder borderRadius="medium">
            <AgGridReact
              {...gridProps}
              rowData={filtered}
              columnDefs={columnDefs}
              getRowId={(p) => groupKey(p.data)}
              loading={loading && !groups.length}
              paginationPageSize={pageSize}
              onGridReady={onGridReady}
              onPaginationChanged={onPaginationChanged}
            />
          </AgGridTableWrapper>
        )}
      </GridCard>

      <Footer
        summary={summary}
        page={pageInfo.page}
        pageCount={pageInfo.pageCount}
        pageSize={pageSize}
        onPage={goToPage}
        onPageSize={setPageSize}
      />

      <SideDrawer
        show={Boolean(editGroup)}
        onClose={() => setEditGroup(null)}
        title="Edit Group"
        subtitle={editGroup?.name || null}
        drawerWidth="520px"
      >
        {editGroup && (
          <GroupForm
            group={editGroup}
            groups={groups}
            onDone={() => {
              setEditGroup(null);
              onReload();
            }}
          />
        )}
      </SideDrawer>

      <SideDrawer
        show={Boolean(membersGroup)}
        onClose={() => setMembersGroup(null)}
        title="Members"
        subtitle={membersGroup?.name || null}
        drawerWidth="560px"
      >
        {membersGroup && (
          <GroupMembersPanel
            group={membersGroup}
            users={users}
            onMutated={(updated) => {
              setMembersGroup(updated);
              onReload();
            }}
          />
        )}
      </SideDrawer>

      <SideDrawer
        show={Boolean(jsonGroup)}
        onClose={() => setJsonGroup(null)}
        title="Group Details"
        subtitle={jsonGroup?.name || null}
        drawerWidth="640px"
      >
        {jsonGroup && <JsonPreview defaultCollapse={3} values={jsonGroup} />}
      </SideDrawer>
    </Box>
  );
}

// ── Create / edit group ──────────────────────────────────────────────────────
// No `group` → POST /groups (create, full body). With `group` → PUT /groups/:id
// (metadata only; the name is the identity key and membership has its own panel).
function GroupForm({ group, groups = [], onDone }) {
  const { createGroup, updateGroup } = useGroupsApi();
  const { enqueueSnackbar } = useSnackbar();
  const editing = Boolean(group);

  const [form, setForm] = useState({
    name: group?.name || "",
    description: group?.description || "",
    businessUnit: group?.businessUnit || "",
    portalActivities: group?.portalActivities || "",
    relatedVendors: group?.relatedVendors || "",
  });
  const [departments, setDepartments] = useState(asArray(group?.departments));
  const [relatedRoles, setRelatedRoles] = useState(
    asArray(group?.relatedRoles),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Options = everything already applied across groups; free-typed values still
  // go. relatedRoles holds job titles ("Processor", "Junior Underwriter"), NOT
  // user.roles keys (preUnderwriter), so ROLE_OPTIONS is deliberately not mixed in.
  const departmentOptions = useMemo(
    () => collectOptions(groups.map((g) => asArray(g.departments))),
    [groups],
  );
  const roleOptions = useMemo(
    () => collectOptions(groups.map((g) => asArray(g.relatedRoles))),
    [groups],
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const metadata = {
        description: form.description,
        businessUnit: form.businessUnit,
        departments,
        relatedRoles,
        portalActivities: form.portalActivities,
        relatedVendors: form.relatedVendors,
      };
      if (editing) {
        await updateGroup(groupKey(group), metadata);
        enqueueSnackbar("Group updated", { variant: "success" });
      } else {
        await createGroup({ name: form.name.trim(), ...metadata });
        enqueueSnackbar(`Group ${form.name.trim()} created`, {
          variant: "success",
        });
      }
      onDone();
    } catch (err) {
      setError(
        err?.response?.data?.detail ??
          err?.message ??
          (editing ? "Update failed" : "Create failed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        <UField
          label="Name"
          required
          helperText={
            editing
              ? "The name is the group's identity key and isn't editable here."
              : "Unique group name (e.g. US Underwriters)."
          }
        >
          <TextField
            value={form.name}
            onChange={set("name")}
            fullWidth
            required
            disabled={editing}
          />
        </UField>
        <UField label="Description">
          <TextField
            value={form.description}
            onChange={set("description")}
            fullWidth
            multiline
            minRows={2}
          />
        </UField>
        <UField label="Business Unit">
          <TextField
            value={form.businessUnit}
            onChange={set("businessUnit")}
            fullWidth
          />
        </UField>
        <UField
          label="Departments"
          helperText="Pick from departments already in use, or type a new one and press Enter."
        >
          <ChecklistAutocomplete
            options={departmentOptions}
            value={departments}
            onChange={setDepartments}
          />
        </UField>
        <UField
          label="Related Roles"
          helperText="Pick from existing roles, or type a new one and press Enter."
        >
          <ChecklistAutocomplete
            options={roleOptions}
            value={relatedRoles}
            onChange={setRelatedRoles}
          />
        </UField>
        <UField label="Portal Activities">
          <TextField
            value={form.portalActivities}
            onChange={set("portalActivities")}
            fullWidth
            multiline
            minRows={2}
          />
        </UField>
        <UField label="Related Vendors">
          <TextField
            value={form.relatedVendors}
            onChange={set("relatedVendors")}
            fullWidth
          />
        </UField>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || (!editing && !form.name.trim())}
            sx={tealBtnSx}
          >
            {submitting
              ? editing
                ? "Saving…"
                : "Creating…"
              : editing
                ? "Save changes"
                : "Create group"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// ── Group members ────────────────────────────────────────────────────────────
// Add (POST /groups/:id/members — resolves email → known user) and remove
// (DELETE /groups/:id/members/:email). Both return the updated group, which we
// bubble up via onMutated so the drawer + grid stay in sync.
// Both add-member inputs are full width and stacked; force left-aligned text so
// they can't inherit a centered alignment from the drawer.
const leftAlignInputSx = {
  width: "100%",
  "& .MuiInputBase-input": { textAlign: "left" },
  "& .MuiFormHelperText-root": { textAlign: "left", ml: 0 },
};

function GroupMembersPanel({ group, users = [], onMutated }) {
  const { addMember, removeMember } = useGroupsApi();
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState("");
  const [memberName, setMemberName] = useState("");
  const [busy, setBusy] = useState(false);
  // Which member's Remove is armed. Removing someone from a group can quietly
  // revoke their access to a whole surface, and the link sits inches from
  // "Edit" — so it asks once, in place. A modal would be heavier than the
  // action deserves.
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState(null);

  const members = asArray(group?.members);

  // Suggestions come from the same /users/all_users fetch that backs the All
  // Users grid; people already in the group are dropped so the dropdown only
  // offers someone you can actually add.
  const memberEmails = useMemo(
    () =>
      new Set(
        asArray(group?.members)
          .map((m) => (m.email || "").toLowerCase())
          .filter(Boolean),
      ),
    [group],
  );
  const userOptions = useMemo(
    () =>
      asArray(users)
        .filter(
          (u) =>
            u?.emailAddress && !memberEmails.has(u.emailAddress.toLowerCase()),
        )
        .sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [users, memberEmails],
  );

  async function add(e) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await addMember(
        groupKey(group),
        addr,
        memberName.trim() || undefined,
      );
      setEmail("");
      setMemberName("");
      enqueueSnackbar(`Added ${addr}`, { variant: "success" });
      onMutated(updated);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Add failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(member) {
    const addr = member.email;
    if (!addr) return;
    setConfirmRemove(null);
    setBusy(true);
    setError(null);
    try {
      const updated = await removeMember(groupKey(group), addr);
      enqueueSnackbar(`Removed ${addr}`, { variant: "default" });
      onMutated(updated);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Remove failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Box component="form" onSubmit={add}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: T.ink500,
            }}
          >
            Add member
          </Box>
          <Stack spacing={2} sx={{ width: "100%" }}>
            <UField
              label="Email"
              required
              helperText="Pick a user or type an email — resolved to a known user by primary email or alias."
            >
              <Autocomplete
                freeSolo
                fullWidth
                autoHighlight
                options={userOptions}
                value={email}
                inputValue={email}
                onInputChange={(_, v) => setEmail(v)}
                onChange={(_, v) => {
                  if (v && typeof v === "object") {
                    setEmail(v.emailAddress || "");
                    const name = fullName(v);
                    setMemberName(name === "—" ? "" : name);
                  } else {
                    setEmail(v || "");
                  }
                }}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : o?.emailAddress || ""
                }
                isOptionEqualToValue={(o, v) =>
                  (o?.emailAddress || "").toLowerCase() ===
                  String(v || "").toLowerCase()
                }
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((o) =>
                    `${fullName(o)} ${o.emailAddress || ""}`
                      .toLowerCase()
                      .includes(q),
                  );
                }}
                renderOption={(props, o) => (
                  <Box
                    component="li"
                    {...props}
                    // `&&` doubles the class specificity so it beats MUI's own
                    // `.MuiAutocomplete-option { align-items: center }` descendant
                    // rule — without it the option text renders centered.
                    sx={{
                      "&&": {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        textAlign: "left",
                        py: 0.75,
                      },
                    }}
                  >
                    <Box
                      sx={{ fontSize: 13, fontWeight: 600, color: T.ink900 }}
                    >
                      {fullName(o)}
                    </Box>
                    <Box sx={{ fontSize: 12, color: T.ink500 }}>
                      {o.emailAddress}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    sx={leftAlignInputSx}
                  />
                )}
              />
            </UField>
            <UField
              label="Full name (optional)"
              helperText="Only used when the email doesn't match an existing user."
            >
              <TextField
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                fullWidth
                sx={leftAlignInputSx}
              />
            </UField>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !email.trim()}
              startIcon={<AddRounded />}
              sx={tealBtnSx}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Divider />

      <Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: T.ink500,
            mb: 1.5,
          }}
        >
          Members ({members.length})
        </Box>
        {members.length === 0 ? (
          <Box sx={{ color: T.ink500, fontSize: 13 }}>No members yet.</Box>
        ) : (
          <Stack spacing={1}>
            {members.map((m, i) => (
              <Box
                key={m.email || m.userId || i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  border: `1px solid ${T.line}`,
                  borderRadius: T.radius,
                  px: 1.25,
                  py: 1,
                }}
              >
                <AvatarDot email={m.email} name={m.fullName || m.email} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      color: T.ink900,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.fullName || m.email || "—"}
                  </Box>
                  {m.email && m.fullName && (
                    <Box
                      sx={{
                        fontSize: 12,
                        color: T.ink500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.email}
                    </Box>
                  )}
                </Box>
                <Tag
                  label={m.matched ? "linked" : "unmatched"}
                  tone={m.matched ? "teal" : "amber"}
                />
                {confirmRemove === m.email ? (
                  <Flx ac g={0.75}>
                    <Box sx={{ fontSize: 12, color: T.ink500 }}>Remove?</Box>
                    <ActionLink onClick={() => remove(m)}>Yes</ActionLink>
                    <ActionLink onClick={() => setConfirmRemove(null)}>
                      Cancel
                    </ActionLink>
                  </Flx>
                ) : (
                  <ActionLink onClick={() => setConfirmRemove(m.email)}>
                    Remove
                  </ActionLink>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
